import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AddCountryService} from '../../../shared/services/common/add-country.service';
import {Country} from '../../../shared/types/country/country';
import {FileRestService} from '../../../shared/services/common/file/file-rest.service';
import {FileUtilsService} from '../../../shared/services/common/file/file-utils.service';
import {SelectItem} from 'primeng/api';
import {LocaleRestService} from '../../../shared/services/locale/locale-rest.service';
import {GlobalMessagesService} from '../../../shared/services/messages/global.messages.service';
import {TranslateService} from '@ngx-translate/core';
import {Locale} from '../../../shared/types/localization/locale';
import {SecurityService} from '../../../shared/services/security/security.service';
import {FileUpload} from 'primeng/fileupload';

@Component({
  selector: 'wsd-add-country',
  templateUrl: './add-country.component.html',
  styleUrls: ['./add-country.component.css']
})
export class AddCountryComponent implements OnInit {

  @Output()
  onSave = new EventEmitter<any>();

  country: Country;

  languageItems: SelectItem[];
  selectedLocaleCode: string;

  imageSrc: any;

  @ViewChild('fileUpload')
  fileUpload: FileUpload;

  private imageFile: File;

  countryForm: UntypedFormGroup;

  display: boolean = false;

  deletedImage: boolean;

  constructor(private addService: AddCountryService,
              private message: GlobalMessagesService,
              private translate: TranslateService,
              private fileRestService: FileRestService,
              private securityService: SecurityService,
              private localeRestService: LocaleRestService,
              private fileUtilsService: FileUtilsService,
              private fb: UntypedFormBuilder) {
  }

  ngOnInit() {
    this.countryForm = this.fb.group({
      region: ['', Validators.required],
      scoutingEstablishedYear: ['', Validators.pattern('^\\d{4}$')],
      iso2Code: [''],
      isoNumericCode: [''],
      iso3Code: [''],
      phoneCode: [''],
      membershipType: [''],
      currency: [''],
      category: ['', Validators.max(255)]
    });
    this.addService.messageSent$.subscribe(
      (country: Country) => {
        this.country = country;
        this.loadLanguages();
        this.countryForm.reset();
        this.countryForm.patchValue(country);
        this.loadImage(this.country.imageId);
        this.display = true;
      }
    );
  }

  loadLanguages() {
    this.localeRestService.getSupportedLanguages().subscribe(locales => {
      this.languageItems = locales.map(value => {
        return {
          label: value.name,
          value: value.code
        };
      });
      this.securityService.getCurrentLocale().subscribe((locale: Locale) => this.selectedLocaleCode = locale.code);
      this.initCountryNames(locales);
    });
  }

  private initCountryNames(locales) {
    if (!this.country.alphabeticNames) {
      this.country.alphabeticNames = [].concat(locales.map(locale => {
        return {langCode: locale.code};
      }));
    }

    if (!this.country.names) {
      this.country.names = [].concat(locales.map(locale => {
        return {langCode: locale.code};
      }));
    }
  }

  private loadImage(imgId: number) {
    if (!imgId) {
      return;
    }
    this.fileRestService.getImage(imgId.toString()).subscribe(
      ((data: any) => {
        this.imageSrc = this.fileUtilsService.getImage(data.content);
        this.deletedImage = false;
      })
    );
  }

  onSelect(event) {
    this.imageSrc = event.files[0].objectURL;
    this.imageFile = event.files[0];
    this.fileUpload.clear();
  }

  save() {
    this.fillEmptyNames();
    if (!this.country.names[0].value || !this.country.alphabeticNames[0].value) {
      this.message.showErrorMessage(this.translate.instant('WS_DIRECTORY.STANDARD_TITLE.FILL_ALL_NAMES'));
      return;
    }

    this.countryForm.markAsDirty();
    Object.keys(this.countryForm.controls).forEach(key => {
      this.countryForm.get(key).markAsDirty();
    });

    if (this.countryForm.invalid) {
      return;
    }

    this.country.region = this.countryForm.get('region').value;
    this.country.scoutingEstablishedYear = this.countryForm.get('scoutingEstablishedYear').value;
    this.country.iso2Code = this.countryForm.get('iso2Code').value;
    this.country.iso3Code = this.countryForm.get('iso3Code').value;
    this.country.isoNumericCode = this.countryForm.get('isoNumericCode').value;
    this.country.phoneCode = this.countryForm.get('phoneCode').value;
    this.country.currency = this.countryForm.get('currency').value;
    this.country.category = this.countryForm.get('category').value;
    this.country.membershipType = this.countryForm.get('membershipType').value;
    this.country.removeImage = this.deletedImage;

    this.display = false;

    this.onSave.emit({
      country: this.country,
      imageFile: this.imageFile
    });
  }

  private fillEmptyNames() {
    this.country.names.forEach((name) => {
      name.value = name.value || this.country.names[0].value;
    });
    this.country.alphabeticNames.forEach((name) => {
      name.value = name.value || this.country.alphabeticNames[0].value;
    });
  }

  removeImage() {
    this.imageSrc = null;
    this.imageFile = null;
    this.deletedImage = true;
    this.fileUpload.clear();
  }

  discard() {
    this.display = false;
  }

}
